// Cache local des conversations de notions/matières.
// Permet à n'importe quel profil de retrouver instantanément l'historique
// d'un chat lié à une notion sans attendre le réseau, et sert de source
// pour la génération PDF.

export type CachedChatMessage = { role: "user" | "assistant"; content: string };

export type CachedChat = {
  messages: CachedChatMessage[];
  conversationId?: string | null;
  subject?: string | null;
  topic?: string | null;
  title?: string | null;
  savedAt: number;
};

const PREFIX = "kelasai_chat_cache_v1::";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours
const INDEX_KEY = "kelasai_chat_cache_index_v1";
const MAX_ENTRIES = 80;

const norm = (v?: string | null) => (v || "").trim().toLowerCase();

export const buildChatCacheKey = (params: {
  userId?: string | null;
  conversationId?: string | null;
  subject?: string | null;
  topic?: string | null;
}) => {
  const user = params.userId || "guest";
  if (params.conversationId) {
    return `${PREFIX}${user}|conv|${params.conversationId}`;
  }
  return `${PREFIX}${user}|notion|${norm(params.subject)}|${norm(params.topic)}`;
};

const readIndex = (): string[] => {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

const writeIndex = (keys: string[]) => {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(keys.slice(0, MAX_ENTRIES)));
  } catch {
    // ignorer quota
  }
};

const touchIndex = (key: string) => {
  const idx = readIndex().filter((k) => k !== key);
  idx.unshift(key);
  if (idx.length > MAX_ENTRIES) {
    const dropped = idx.slice(MAX_ENTRIES);
    dropped.forEach((k) => {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    });
  }
  writeIndex(idx.slice(0, MAX_ENTRIES));
};

export const readChatCache = (key: string): CachedChat | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedChat;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writeChatCache = (key: string, payload: Omit<CachedChat, "savedAt">) => {
  try {
    const trimmed: CachedChat = {
      ...payload,
      messages: payload.messages.slice(-200),
      savedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(trimmed));
    touchIndex(key);
  } catch {
    // quota dépassé : purger les plus anciens et retenter une fois
    try {
      const idx = readIndex();
      const toRemove = idx.slice(Math.floor(MAX_ENTRIES / 2));
      toRemove.forEach((k) => localStorage.removeItem(k));
      writeIndex(idx.slice(0, Math.floor(MAX_ENTRIES / 2)));
      localStorage.setItem(key, JSON.stringify({ ...payload, savedAt: Date.now() }));
      touchIndex(key);
    } catch {
      /* on abandonne */
    }
  }
};

export const deleteChatCache = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
  writeIndex(readIndex().filter((k) => k !== key));
};
